import { useState, useEffect, useMemo, useRef, lazy, Suspense } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import CreatePostIcon from "assets/icons/create-post.svg";
import LibraryCreatePostIcon from "assets/icons/library-create-post.svg";
import LibraryImageIcon from "assets/icons/library-image.svg";
import LibraryStarIcon from "assets/icons/library-star.svg";
import { CollectionFile } from "shared/types/files";
import { useGridMediaCard } from "hooks/useGridMediaCard";
import { entitlementsPermission } from "@/utils/entitlements-permission";
import { useDispatch, useSelector } from "react-redux";
import { getModals, getUser } from "@/selectors/commonSelectors";
import { hideModal, showEditAssetModal } from "actions/modal";
import { ContentCollectBadge } from "../content-collect";

const CreativePhotoEditorModal = lazy(() =>
  import("ui/photo-editor").then((m) => ({
    default: m.CreativePhotoEditorModal,
  }))
);

const CreativeVideoEditorModal = lazy(() =>
  import("ui/video-editor").then((m) => ({
    default: m.CreativeVideoEditorModal,
  }))
);

export interface GridMediaCardProps {
  asset: CollectionFile;
  className?: string;
  showCollectionTooltip?: boolean;
  order?: number;
  onCardClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  onCheckboxClick?: React.MouseEventHandler<HTMLButtonElement>;
  openDetailsOnClick?: boolean;
  previewed?: boolean;
  hoverContent?: React.ReactNode; // Custom hover overlay content
  canSelect?: boolean; // Controls checkbox visibility (default: true)
  canFavorite?: boolean; // Controls favorite star visibility (default: true)
  selected?: boolean; // External selection state override
  selecting?: boolean; // External selecting mode override
  showActionButtons?: boolean; // Controls action buttons visibility (default: true)
  showCheckbox?: boolean; // Controls checkbox render (default: true)
  showFilename?: boolean; // Controls filename footer visibility (default: true)
  username?: string; // Username to display in upper right (for feed/hashtag media)
  isSourceMedia?: boolean; // Controls checkbox position (top-right when true for media picker)
}

// Moved outside component to avoid recreating on every render
const createCheckboxClasses = (
  selected: boolean,
  selecting: boolean,
  isSourceMedia?: boolean,
  compact = false
) => {
  // Use cn utility for better class composition
  // Position: top-right for media picker (isSourceMedia), top-left for standalone library
  const positionClasses = isSourceMedia ? "top-2 right-2" : "top-2 left-2";
  const baseClasses = `absolute ${positionClasses} ${compact ? "size-4" : "size-8"} rounded-md flex items-center justify-center z-[3] transition-opacity duration-200`;

  // For source media (picker mode): always visible for clarity
  // For library mode: always visible when selected or selecting, hover-only otherwise
  if (isSourceMedia) {
    return `${baseClasses} opacity-100`;
  }

  // Always visible when selected or in selecting mode
  if (selected || selecting) {
    return `${baseClasses} opacity-100`;
  }

  // Hidden by default, visible on group hover
  return `${baseClasses} opacity-0 group-hover:opacity-100`;
};

const getAssetIconClass = (asset: CollectionFile) => {
  const isPdf = asset.meta?.mime === "application/pdf";

  if (isPdf) {
    return "fa-light fa-file-pdf";
  }

  if (asset.meta?.isVideo) {
    return "fa-light fa-video";
  }

  return "fa-light fa-image";
};

const getUsedTagTooltipText = (asset: CollectionFile) => {
  let fileType = "media";
  if (asset.meta?.isVideo) {
    fileType = "video";
  } else if (asset.meta?.isImage) {
    fileType = "image";
  } else if (asset.meta?.mime === "application/pdf") {
    fileType = "pdf";
  }

  return `This ${fileType} is being used in a draft or upcoming post.`;
};

/**
 * Smart filename truncation that preserves file extensions
 * Examples:
 *   "very-long-filename-here.jpg" -> "very-long-fil...jpg"
 *   "IMG_0183.jpg" -> "IMG_0183.jpg" (unchanged)
 *   "test" -> "test" (no extension)
 */
const truncateFilename = (filename: string, maxLength: number = 20): string => {
  if (filename.length <= maxLength) return filename;

  const lastDotIndex = filename.lastIndexOf(".");

  // No extension or extension is too long
  if (lastDotIndex === -1 || filename.length - lastDotIndex > 6) {
    return filename.substring(0, maxLength - 3) + "...";
  }

  const extension = filename.substring(lastDotIndex);
  const nameWithoutExt = filename.substring(0, lastDotIndex);
  const maxNameLength = maxLength - extension.length - 3; // -3 for "..."

  if (maxNameLength <= 0) return filename.substring(0, maxLength - 3) + "...";

  return nameWithoutExt.substring(0, maxNameLength) + "..." + extension;
};

const GridMediaCard = ({
  asset,
  className = "",
  showCollectionTooltip = false,
  order = 1,
  onCardClick,
  onCheckboxClick,
  openDetailsOnClick = false,
  previewed = false,
  hoverContent,
  canSelect,
  canFavorite,
  selected: externalSelected,
  selecting: externalSelecting,
  showActionButtons = true,
  showCheckbox = true,
  showFilename = true,
  username,
  isSourceMedia = false,
}: GridMediaCardProps) => {
  // Handle undefined asset gracefully
  if (!asset) {
    return (
      <div
        className={cn(
          "relative aspect-square bg-neutral-200 rounded-lg overflow-hidden",
          className
        )}
      >
        <div className="absolute inset-0 flex items-center justify-center">
          <i className="fa-light fa-image text-neutral-400 text-2xl" />
        </div>
      </div>
    );
  }

  const {
    id,
    selected: hookSelected,
    selecting: hookSelecting,
    assetUrl,
    gifPreviewUrl,
    lowQualityVideoUrl,
    favorited,
    isVideo,
    assetIsUsed,
    isEditable,
    canSelect: hookCanSelect,
    canFavorite: hookCanFavorite,
    isManagedCollection,
    showPhotoEditor,
    photoEditorImageUrl,
    photoEditorAssetId,
    photoEditorAssetUrl,
    photoEditorIsReplacingOriginal,
    photoEditorProgress,
    photoEditorSaveButtonText,
    photoEditorMessage,
    photoEditorIsSaving,
    showVideoEditor,
    videoEditorVideoUrl,
    videoEditorAssetId,
    videoEditorAssetUrl,
    videoEditorIsReplacingOriginal,
    videoEditorProgress,
    videoEditorSaveButtonText,
    videoEditorMessage,
    videoEditorIsSaving,

    handleCardClick,
    handleFavoriteClick,
    handleCreatePost,
    handleCopy,
    handleMove,
    handleDelete,
    handleViewDetails,
    handleEdit,

    handlePhotoEditorClose,
    handlePhotoEditorSave,
    handleVideoEditorClose,
    handleVideoEditorSave,
  } = useGridMediaCard(asset, onCardClick, {
    canSelect,
    canFavorite,
  });

  // Use external selection state if provided, otherwise use hook values
  const selected =
    externalSelected !== undefined ? externalSelected : hookSelected;
  const selecting =
    externalSelecting !== undefined ? externalSelecting : hookSelecting;

  const [showVideoPreview, setShowVideoPreview] = useState(false);
  const [actionMenuOpen, setActionMenuOpen] = useState(false);
  const dispatch = useDispatch();
  const detailsModal = useSelector(getModals).find(
    (modal: { modalType: string }) =>
      modal.modalType === "EDIT_COLLECTION_ASSET"
  );
  const runLibraryAction = (action: () => void) => {
    const details = detailsModal?.modalProps;
    if (openDetailsOnClick && details?.asset) {
      dispatch(hideModal(false, "EDIT_COLLECTION_ASSET"));
      dispatch(showEditAssetModal(details.asset, details.isReadOnly, action));
    } else {
      action();
    }
  };
  const [videoPreviewLoaded, setVideoPreviewLoaded] = useState(false);
  const [gifPreviewLoaded, setGifPreviewLoaded] = useState(false);
  const [videoPreviewError, setVideoPreviewError] = useState(false);
  const [gifPreviewError, setGifPreviewError] = useState(false);
  const videoPreviewRef = useRef<HTMLVideoElement | null>(null);
  const user = useSelector(getUser);
  const gifPreviewSrc =
    gifPreviewUrl || assetUrl.replace("thumbnail.jpg", "gif.gif");
  const canUseInlineVideoPreview = Boolean(
    lowQualityVideoUrl && !videoPreviewError
  );
  const canUseGifPreview = Boolean(
    !canUseInlineVideoPreview && gifPreviewUrl && !gifPreviewError
  );
  const showInlineVideoPreview =
    isVideo &&
    showVideoPreview &&
    canUseInlineVideoPreview &&
    videoPreviewLoaded;
  const showAnimatedGifPreview =
    isVideo &&
    showVideoPreview &&
    canUseGifPreview &&
    gifPreviewLoaded &&
    !gifPreviewError;
  const showThumbnailImage =
    !isVideo ||
    !showVideoPreview ||
    (!showInlineVideoPreview && !showAnimatedGifPreview);

  const tooltipPlacement =
    order % 5 === 4 || order % 5 === 0 ? "left" : "right";

  // Memoize checkbox classes to avoid recalculating on every render
  const checkboxClasses = useMemo(
    () =>
      createCheckboxClasses(
        selected,
        selecting,
        isSourceMedia,
        openDetailsOnClick
      ),
    [selected, selecting, isSourceMedia, openDetailsOnClick]
  );

  useEffect(() => {
    setVideoPreviewLoaded(false);
    setVideoPreviewError(false);
  }, [lowQualityVideoUrl]);

  useEffect(() => {
    setGifPreviewLoaded(false);
    setGifPreviewError(false);
  }, [gifPreviewSrc]);

  // Preload GIF preview only when we need the legacy fallback.
  useEffect(() => {
    if (isVideo && canUseGifPreview && !gifPreviewLoaded && !gifPreviewError) {
      const img = new Image();
      img.addEventListener("load", () => setGifPreviewLoaded(true));
      img.addEventListener("error", () => setGifPreviewError(true));
      img.src = gifPreviewSrc;
    }
  }, [
    isVideo,
    canUseGifPreview,
    gifPreviewLoaded,
    gifPreviewError,
    gifPreviewSrc,
  ]);

  useEffect(() => {
    const previewVideo = videoPreviewRef.current;
    if (!previewVideo) {
      return;
    }

    if (showVideoPreview && canUseInlineVideoPreview) {
      previewVideo.play().catch(() => {
        // Playback interrupted (e.g. quick hover-out); ignore.
      });
    } else {
      previewVideo.pause();
      previewVideo.currentTime = 0;
    }
  }, [showVideoPreview, canUseInlineVideoPreview]);

  return (
    <div
      className={cn(
        "relative",
        selected &&
          !openDetailsOnClick &&
          "bg-blue-50 rounded-lg ring-4 ring-blue-500",
        className
      )}
      data-testid="assetContainer"
    >
      <div
        className={cn(
          "relative w-full",
          openDetailsOnClick && showFilename ? "h-[236px]" : "aspect-square"
        )}
      >
        <div className="absolute inset-0 w-full h-full group">
          <div
            className={cn(
              "relative w-full h-full cursor-pointer rounded-lg overflow-hidden flex flex-col",
              "transition-shadow duration-200",
              openDetailsOnClick
                ? cn(
                    "bg-white hover:bg-blue-25",
                    actionMenuOpen && "bg-blue-25",
                    previewed && "bg-blue-50 shadow-sm hover:bg-blue-50",
                    selected && "bg-blue-25"
                  )
                : selected
                  ? ""
                  : "border border-neutral-100 hover:shadow-lg"
            )}
            data-library-asset-details={
              openDetailsOnClick ? asset._id : undefined
            }
            onClick={(e) =>
              openDetailsOnClick ? handleViewDetails() : handleCardClick(e)
            }
            onMouseEnter={() => isVideo && setShowVideoPreview(true)}
            onMouseLeave={() => isVideo && setShowVideoPreview(false)}
          >
            <div
              className={cn(
                "bg-neutral-100 w-full relative flex items-center justify-center overflow-hidden",
                openDetailsOnClick && showFilename
                  ? "h-[200px] shrink-0"
                  : "flex-1"
              )}
            >
              {/* Blurred backdrop preserves the square grid while showing the full crop in front. */}
              <img
                aria-hidden="true"
                className={`absolute inset-0 z-[0] h-full w-full scale-125 object-cover object-center blur-sm transition-opacity duration-300 ease-in-out ${showThumbnailImage ? "opacity-100" : "opacity-0"}`}
                style={{ WebkitUserDrag: "none" } as React.CSSProperties}
                src={assetUrl}
                alt=""
              />
              <div className="absolute inset-0 bg-black/5 z-[1]" />

              {/* Thumbnail Image - Always rendered */}
              <img
                className={`absolute inset-0 h-full w-full object-contain object-center max-w-full max-h-full select-none transition-opacity duration-300 ease-in-out ${showThumbnailImage ? "opacity-100 z-[2]" : "opacity-0 z-[1]"}`}
                style={{ WebkitUserDrag: "none" } as React.CSSProperties}
                src={assetUrl}
                alt={`Media asset ${id}`}
                data-testid="gridMediaCardImage"
              />

              {/* Video Preview - Always rendered for videos */}
              {isVideo && (
                <>
                  {lowQualityVideoUrl && (
                    <video
                      ref={videoPreviewRef}
                      className={`absolute inset-0 h-full w-full object-contain object-center transition-opacity duration-300 ease-in-out ${showInlineVideoPreview ? "opacity-100 z-[2]" : "opacity-0 z-[1]"}`}
                      src={lowQualityVideoUrl}
                      muted
                      loop
                      playsInline
                      preload="none"
                      onLoadedData={() => setVideoPreviewLoaded(true)}
                      onError={() => setVideoPreviewError(true)}
                      data-testid="gridMediaCardVideoPreview"
                    />
                  )}
                  {canUseGifPreview && (
                    <>
                      <img
                        aria-hidden="true"
                        className={`absolute inset-0 z-[0] h-full w-full scale-110 object-cover object-center blur-2xl transition-opacity duration-300 ease-in-out ${showAnimatedGifPreview ? "opacity-100" : "opacity-0"}`}
                        style={
                          { WebkitUserDrag: "none" } as React.CSSProperties
                        }
                        src={gifPreviewSrc}
                        alt=""
                      />
                      <img
                        className={`absolute inset-0 h-full w-full object-contain object-center transition-opacity duration-300 ease-in-out ${showAnimatedGifPreview ? "opacity-100 z-[2]" : "opacity-0 z-[1]"}`}
                        style={
                          { WebkitUserDrag: "none" } as React.CSSProperties
                        }
                        src={gifPreviewSrc}
                        alt="Video preview"
                        data-testid="gridMediaCardGifPreview"
                      />
                    </>
                  )}
                </>
              )}

              {/* Play Button for Videos */}
              {isVideo &&
                (!showVideoPreview ||
                  (!showInlineVideoPreview && !showAnimatedGifPreview)) && (
                  <div
                    className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-[3]"
                    style={{
                      width: 0,
                      height: 0,
                      borderStyle: "solid",
                      borderWidth: "13px 0 13px 20px",
                      borderColor:
                        "transparent transparent transparent #ffffff",
                    }}
                  />
                )}

              {/* Custom Hover Overlay */}
              {hoverContent && (
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-black/60 z-[2] rounded-lg overflow-hidden">
                  {hoverContent}
                </div>
              )}

              {/* Used flag */}
              {assetIsUsed && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="absolute left-2 bottom-2 rounded-full bg-black/40 flex items-center justify-center text-white text-base font-medium px-2 py-2 z-[2]">
                      <i className="fa-regular fa-check-to-slot"></i>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent
                    side="top"
                    className="max-w-[140px] text-xs text-center"
                  >
                    {getUsedTagTooltipText(asset)}
                  </TooltipContent>
                </Tooltip>
              )}

              {/* Content Collect provenance badge (bottom-right; the used
                  flag owns bottom-left). Self-gating — renders nothing for
                  assets that didn't come from a collect. */}
              <ContentCollectBadge asset={asset} />

              {/* Username badge for feed/hashtag media */}
              {username && (
                <div className="absolute right-2 top-2 rounded-md bg-black/50 backdrop-blur-sm flex items-center text-white text-xs font-medium px-2 py-1 z-[2] max-w-[calc(100%-16px)] overflow-hidden">
                  <span className="truncate">@{username}</span>
                </div>
              )}
            </div>

            {showFilename && (
              <div
                className={cn(
                  "flex h-9 shrink-0 items-center justify-between p-2 gap-2 w-full",
                  !openDetailsOnClick &&
                    (selected
                      ? "bg-blue-50"
                      : "bg-white border-t border-neutral-100")
                )}
              >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    {openDetailsOnClick &&
                    !asset.meta?.isVideo &&
                    asset.meta?.mime !== "application/pdf" ? (
                      <LibraryImageIcon className="size-5" />
                    ) : (
                      <i
                        className={`${getAssetIconClass(asset)} text-neutral-800 ${openDetailsOnClick ? "text-base" : "text-sm"}`}
                      />
                    )}
                  </div>
                  {(asset?.name || "Untitled").length > 20 ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div
                          className={cn(
                            "font-inter font-normal text-neutral-800 whitespace-nowrap overflow-hidden text-ellipsis min-w-0 cursor-default",
                            openDetailsOnClick ? "text-sm leading-5" : "text-xs"
                          )}
                        >
                          {truncateFilename(asset?.name || "Untitled")}
                        </div>
                      </TooltipTrigger>
                      <TooltipContent
                        side="top"
                        className="max-w-[300px] break-all"
                      >
                        {asset?.name}
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    <div
                      className={cn(
                        "font-inter font-normal text-neutral-800 whitespace-nowrap overflow-hidden text-ellipsis min-w-0",
                        openDetailsOnClick ? "text-sm leading-5" : "text-xs"
                      )}
                    >
                      {asset?.name || "Untitled"}
                    </div>
                  )}
                </div>

                {hookCanFavorite && (
                  <button
                    type="button"
                    aria-label={
                      favorited ? "Remove from favourites" : "Add to favourites"
                    }
                    aria-pressed={favorited}
                    className={cn(
                      "w-5 h-5 flex items-center justify-center cursor-pointer flex-shrink-0 border-0 bg-transparent p-0 focus-visible:opacity-100",
                      openDetailsOnClick &&
                        !favorited &&
                        !previewed &&
                        !selected &&
                        !actionMenuOpen &&
                        "opacity-0 group-hover:opacity-100"
                    )}
                    onClick={handleFavoriteClick}
                    data-testid="favourite-button"
                  >
                    {openDetailsOnClick && !favorited ? (
                      <LibraryStarIcon className="size-5" />
                    ) : (
                      <i
                        className={cn(
                          "text-yellow-500",
                          favorited ? "fa-solid fa-star" : "fa-regular fa-star"
                        )}
                      />
                    )}
                  </button>
                )}
              </div>
            )}
          </div>

          {openDetailsOnClick && (
            <button
              type="button"
              aria-label={`View details for ${asset.name || "Untitled asset"}`}
              onClick={handleViewDetails}
              className="pointer-events-none absolute inset-0 rounded-lg border-0 bg-transparent p-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-neutral-900"
            />
          )}

          {/* Overlay Controls */}
          {hookCanSelect && showCheckbox && (
            <div
              className={cn(
                checkboxClasses,
                openDetailsOnClick && actionMenuOpen && "opacity-100"
              )}
            >
              <Checkbox
                data-testid="select-button"
                checked={selected}
                onClick={(event) => {
                  event.stopPropagation();
                  if (onCheckboxClick) onCheckboxClick(event);
                  else handleCardClick();
                }}
                className={cn(
                  "bg-white shadow-sm data-[state=checked]:bg-blue-500 p-0",
                  openDetailsOnClick ? "size-4 rounded-[6px]" : "size-5"
                )}
              />
            </div>
          )}

          {/* Action Buttons */}
          {showActionButtons && !selected && !selecting && (
            <div
              data-library-card-actions={openDetailsOnClick || undefined}
              className={cn(
                "absolute top-2 right-2 opacity-0 group-hover:opacity-100 flex gap-2 z-[3] transition-opacity duration-200",
                openDetailsOnClick && actionMenuOpen && "opacity-100"
              )}
            >
              <button
                aria-label="Create post"
                className={cn(
                  "w-8 h-8 flex items-center justify-center cursor-pointer p-0 shadow-sm",
                  openDetailsOnClick
                    ? "bg-[#ED207C] rounded-lg hover:bg-[#d91d6e]"
                    : "bg-red-500 rounded-full hover:bg-red-600"
                )}
                onClick={(event) =>
                  runLibraryAction(() => handleCreatePost(event))
                }
              >
                {openDetailsOnClick ? (
                  <LibraryCreatePostIcon className="size-5" />
                ) : (
                  <div className="size-4 [&_svg]:size-4">
                    <CreatePostIcon />
                  </div>
                )}
              </button>

              {/* Action Menu - shadcn DropdownMenu */}
              <DropdownMenu onOpenChange={setActionMenuOpen}>
                <DropdownMenuTrigger asChild>
                  <button
                    aria-label="Asset actions"
                    className={cn(
                      "w-8 h-8 bg-white border border-neutral-200 rounded-lg hover:bg-neutral-50 hover:border-neutral-300 hover:shadow-md flex items-center justify-center p-0 transition-all duration-150",
                      openDetailsOnClick && actionMenuOpen && "bg-blue-50"
                    )}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <i className="fa-solid fa-ellipsis-vertical text-neutral-500 text-sm hover:text-neutral-700" />
                  </button>
                </DropdownMenuTrigger>

                <DropdownMenuContent
                  data-library-card-actions={openDetailsOnClick || undefined}
                  align="end"
                  sideOffset={openDetailsOnClick ? 6 : 4}
                  size={openDetailsOnClick ? "sm" : undefined}
                  className="w-[160px]"
                >
                  {entitlementsPermission.hasEditorLibrary(user) &&
                    isEditable && (
                      <DropdownMenuItem
                        size={openDetailsOnClick ? "sm" : undefined}
                        onSelect={() => runLibraryAction(() => handleEdit())}
                      >
                        <i className="fa-regular fa-pen-to-square w-5 text-base text-neutral-800" />
                        Edit
                      </DropdownMenuItem>
                    )}

                  <DropdownMenuItem
                    size={openDetailsOnClick ? "sm" : undefined}
                    onSelect={() => runLibraryAction(() => handleMove())}
                    disabled={isManagedCollection}
                  >
                    <i className="fa-regular fa-arrows-up-down-left-right w-5 text-base text-neutral-800" />
                    Move
                  </DropdownMenuItem>

                  <DropdownMenuItem
                    size={openDetailsOnClick ? "sm" : undefined}
                    onSelect={() => runLibraryAction(() => handleCopy())}
                  >
                    <i className="fa-regular fa-clone w-5 text-base text-neutral-800" />
                    {openDetailsOnClick ? "Duplicate" : "Copy"}
                  </DropdownMenuItem>

                  <DropdownMenuItem
                    size={openDetailsOnClick ? "sm" : undefined}
                    onSelect={() => handleViewDetails()}
                  >
                    <i className="fa-regular fa-square-info w-5 text-base text-neutral-800" />
                    Details
                  </DropdownMenuItem>

                  {!openDetailsOnClick && <DropdownMenuSeparator />}
                  <DropdownMenuItem
                    size={openDetailsOnClick ? "sm" : undefined}
                    onSelect={() => runLibraryAction(() => handleDelete())}
                    disabled={isManagedCollection}
                    className="text-red-600 focus:text-red-600"
                  >
                    <i className="fa-regular fa-trash-can w-5 text-base" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}

          {openDetailsOnClick && (
            <div
              className={cn(
                "pointer-events-none absolute inset-0 z-[4] rounded-lg border border-neutral-100 group-hover:border-2 group-hover:border-blue-50",
                actionMenuOpen && "border-2 border-blue-50",
                previewed &&
                  "border-2 border-blue-100 group-hover:border-blue-100",
                selected &&
                  "border-2 border-blue-500 group-hover:border-blue-500"
              )}
            />
          )}

          {/* Selection Border */}
          {selected && !openDetailsOnClick && (
            <div className="absolute -inset-0.5 border-2 border-blue-500 rounded-xl pointer-events-none" />
          )}
        </div>
      </div>

      {/* Collection Tooltip */}
      {showCollectionTooltip && (
        <div
          className={cn(
            "absolute w-48 bg-neutral-900 text-white p-2 rounded opacity-100 z-10",
            tooltipPlacement === "left" ? "right-full" : "left-full"
          )}
        >
          Please select a Collection
        </div>
      )}

      {/* Photo Editor Modal */}
      {showPhotoEditor && (
        <Suspense fallback={null}>
          <CreativePhotoEditorModal
            show={showPhotoEditor}
            image={photoEditorImageUrl}
            assetId={photoEditorAssetId}
            assetUrl={photoEditorAssetUrl}
            isReplacingOriginal={photoEditorIsReplacingOriginal}
            onClose={handlePhotoEditorClose}
            onSave={handlePhotoEditorSave}
            progress={photoEditorProgress}
            saveButtonText={photoEditorSaveButtonText}
            message={photoEditorMessage}
            isSaving={photoEditorIsSaving}
          />
        </Suspense>
      )}

      {/* Video Editor Modal */}
      {showVideoEditor && (
        <Suspense fallback={null}>
          <CreativeVideoEditorModal
            show={showVideoEditor}
            video={videoEditorVideoUrl}
            assetId={videoEditorAssetId}
            assetUrl={videoEditorAssetUrl}
            isReplacingOriginal={videoEditorIsReplacingOriginal}
            onClose={handleVideoEditorClose}
            onSave={handleVideoEditorSave}
            progress={videoEditorProgress}
            saveButtonText={videoEditorSaveButtonText}
            message={videoEditorMessage}
            isSaving={videoEditorIsSaving}
          />
        </Suspense>
      )}
    </div>
  );
};

export default GridMediaCard;
